import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useMobileSearchAPI,
  SearchType,
  SearchResponse,
} from './useMobileSearchAPI'
import {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../../../models/companies'
import { SEARCH_TYPE_LABELS } from './searchTypeConstants'

const MIN_SEARCH_CHARS = 2
const DEBOUNCE_MS = 500

export interface MobileSearchFilters {
  category?: string
  type?: string
}

export type SearchResult = ContactCompanySearchResult | DoorSearchResult

// Type guard for discriminating search results
const isDoorSearchResult = (
  result: SearchResult
): result is DoorSearchResult => {
  return 'id' in result && typeof result.id === 'number'
}

// Helper function to get unique ID from search results
const getResultId = (result: SearchResult): string | number | undefined => {
  if (isDoorSearchResult(result)) {
    // DoorSearchResult has id (number) and item_id (string)
    return result.id || result.item_id
  }
  // ContactCompanySearchResult has contact_company_id (number) or item_id (string | number)
  return result.contact_company_id || result.item_id
}

export interface MobileSearchState {
  searchType: SearchType
  query: string
  filters: MobileSearchFilters
  sort: string
  results: SearchResult[]
  total: number
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  error: string | null
  searchTypeLabel: string
  isDebouncing: boolean
}

interface UseMobileSearchReturn {
  state: MobileSearchState
  setSearchType: (searchType: SearchType) => void
  setQuery: (query: string) => void
  setFilters: (filters: MobileSearchFilters) => void
  setSort: (sort: string) => void
  removeFilter: (filterKey: 'category' | 'type', valueToRemove?: string) => void
  clearSearch: () => void
  loadMore: () => void
}

export const useMobileSearch = (): UseMobileSearchReturn => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { performSearch } = useMobileSearchAPI()
  const abortControllerRef = useRef<AbortController | null>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isInternalUpdateRef = useRef<boolean>(false)
  const skipFilterSortEffectRef = useRef<boolean>(false)

  // Initialize state from URL params
  const searchType =
    (searchParams.get('searchType') as SearchType) || 'fb_company'
  const query = searchParams.get('q') || ''
  const category = searchParams.get('category') || undefined
  const type = searchParams.get('type') || undefined
  const sort = searchParams.get('sort') || 'relevance'

  const [state, setState] = useState<MobileSearchState>({
    searchType,
    query,
    filters: { category, type },
    sort,
    results: [],
    total: 0,
    isLoading: false,
    isLoadingMore: false,
    hasMore: false,
    error: null,
    searchTypeLabel: SEARCH_TYPE_LABELS[searchType],
    isDebouncing: false,
  })

  // Sync state with URL params when they change externally
  useEffect(() => {
    // Skip if this URL change was triggered by our own updateURLParams
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false
      return
    }

    const urlSearchType =
      (searchParams.get('searchType') as SearchType) || 'fb_company'
    const urlQuery = searchParams.get('q') || ''
    const urlCategory = searchParams.get('category') || undefined
    const urlType = searchParams.get('type') || undefined
    const urlSort = searchParams.get('sort') || 'relevance'

    setState((prev) => ({
      ...prev,
      searchType: urlSearchType,
      query: urlQuery,
      filters: { category: urlCategory, type: urlType },
      sort: urlSort,
      searchTypeLabel: SEARCH_TYPE_LABELS[urlSearchType],
    }))
  }, [searchParams])

  // Update URL params when state changes
  const updateURLParams = useCallback(
    (updates: Partial<MobileSearchState>): void => {
      const newParams = new URLSearchParams(searchParams)

      if (updates.searchType !== undefined) {
        newParams.set('searchType', updates.searchType)
      }
      if (updates.query !== undefined) {
        if (updates.query) {
          newParams.set('q', updates.query)
        } else {
          newParams.delete('q')
        }
      }
      if (updates.filters !== undefined) {
        // Handle category filter
        if ('category' in updates.filters) {
          if (updates.filters.category) {
            newParams.set('category', updates.filters.category)
          } else {
            newParams.delete('category')
          }
        }
        // Handle type filter
        if ('type' in updates.filters) {
          if (updates.filters.type) {
            newParams.set('type', updates.filters.type)
          } else {
            newParams.delete('type')
          }
        }
      }
      if (updates.sort !== undefined) {
        newParams.set('sort', updates.sort)
      }

      // Mark as internal update to prevent circular sync
      isInternalUpdateRef.current = true
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Stable ref to hold the latest executeSearch function
  const executeSearchRef = useRef<
    | ((
        searchType: SearchType,
        query: string,
        filters: MobileSearchFilters,
        sort: string,
        append?: boolean
      ) => Promise<void>)
    | undefined
  >(undefined)

  // Perform the actual search
  const executeSearch = useCallback(
    async (
      searchType: SearchType,
      query: string,
      filters: MobileSearchFilters,
      sort: string,
      append: boolean = false
    ): Promise<void> => {
      // Don't search if query is too short
      if (!query || query.length < MIN_SEARCH_CHARS) {
        setState((prev) => ({
          ...prev,
          results: [],
          total: 0,
          isLoading: false,
          isLoadingMore: false,
          hasMore: false,
          error: null,
        }))
        return
      }

      // Cancel previous search and create new abort controller
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      // Get current offset from state and set loading state
      // Clear error immediately to prevent flash of old error
      let currentOffset = 0
      setState((prev) => {
        currentOffset = append ? prev.results.length : 0
        // Only update state if needed (prevent unnecessary re-renders)
        if (append) {
          return {
            ...prev,
            isLoadingMore: true,
            error: null,
          }
        } else {
          return {
            ...prev,
            isLoading: true,
            error: null,
          }
        }
      })

      try {
        const options: {
          category_filter?: string
          type_filter?: string
          sort_by: string
          limit: number
          offset: number
        } = {
          sort_by: sort,
          limit: 100,
          offset: currentOffset,
        }

        // Only add filters if they have actual values (not undefined)
        if (filters.category) {
          options.category_filter = filters.category
        }
        if (filters.type) {
          options.type_filter = filters.type
        }

        const response: SearchResponse = await performSearch(
          searchType,
          query,
          options,
          abortControllerRef.current.signal
        )

        setState((prev) => {
          // Deduplicate results when appending
          let newResults = response.results
          if (append) {
            // Create a Map of existing IDs (memoize getResultId calls)
            const existingIdsMap = new Map<string | number, boolean>()
            prev.results.forEach((r) => {
              const id = getResultId(r)
              if (id !== undefined) {
                existingIdsMap.set(id, true)
              }
            })

            // Filter out duplicates from new results (call getResultId only once per result)
            const uniqueNewResults = response.results.filter((r) => {
              const id = getResultId(r)
              return id !== undefined && !existingIdsMap.has(id)
            })

            newResults = [...prev.results, ...uniqueNewResults]
          }

          return {
            ...prev,
            results: newResults,
            total: response.total,
            hasMore: newResults.length < response.total,
            isLoading: false,
            isLoadingMore: false,
            error: null,
          }
        })
      } catch (error) {
        // Check for both AbortError (fetch API) and CanceledError (Axios)
        const isAborted =
          error instanceof Error &&
          (error.name === 'AbortError' || error.name === 'CanceledError')

        if (isAborted) {
          // Aborted request - DO NOT reset loading states
          // Another search is already in progress (that's why this was aborted)
          // Resetting isLoading here causes a flash of "no results"
          // Don't update state at all - let the new search handle loading state
          return
        } else {
          // Real error - show error message
          setState((prev) => ({
            ...prev,
            results: append ? prev.results : [],
            total: append ? prev.total : 0,
            isLoading: false,
            isLoadingMore: false,
            error: 'Please try again.',
          }))
        }
      }
    },
    [performSearch]
  )

  // Keep ref up to date with latest executeSearch
  useEffect(() => {
    executeSearchRef.current = executeSearch
  }, [executeSearch])

  // Debounced search effect for query changes only
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set debouncing state if query is long enough
    if (state.query && state.query.length >= MIN_SEARCH_CHARS) {
      setState((prev) => ({ ...prev, isDebouncing: true }))

      // Set new timer for debounced search
      debounceTimerRef.current = setTimeout(() => {
        // Mark that we're executing from debounce to prevent filter/sort effect from firing
        // IMPORTANT: Set this BEFORE any state updates
        skipFilterSortEffectRef.current = true
        // Only set isDebouncing to false - let executeSearch handle isLoading
        // This prevents double setState causing isLoading to end up false
        setState((prev) => ({ ...prev, isDebouncing: false }))
        // Call through ref to avoid dependency on executeSearch
        // executeSearch will set isLoading: true
        executeSearchRef.current?.(
          state.searchType,
          state.query,
          state.filters,
          state.sort
        )
        // Reset the skip flag after effects have settled
        // Use longer delay to ensure filter/sort effect has run and checked the flag
        setTimeout(() => {
          skipFilterSortEffectRef.current = false
        }, 100)
      }, DEBOUNCE_MS)
    } else {
      // Query too short, clear results immediately
      setState((prev) => ({ ...prev, isDebouncing: false }))
    }

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [state.searchType, state.query])

  // Immediate search for filter/sort changes (no debounce)
  // NOTE: This effect should ONLY run when filters or sort change
  // Query and searchType changes are handled by the debounce effect above
  useEffect(() => {
    // Skip if we just executed from debounce timeout
    if (skipFilterSortEffectRef.current) {
      return
    }

    // Only execute if query is valid and not currently debouncing
    if (
      state.query &&
      state.query.length >= MIN_SEARCH_CHARS &&
      !state.isDebouncing
    ) {
      // Set loading state before executing to prevent flash of "no results"
      setState((prev) => ({ ...prev, isLoading: true }))
      executeSearchRef.current?.(
        state.searchType,
        state.query,
        state.filters,
        state.sort
      )
    }
  }, [
    state.filters,
    state.sort,
    state.isDebouncing,
    // NOTE: Removed state.searchType and state.query from dependencies
    // These should only trigger debounce effect, not immediate filter/sort search
  ])

  // Set search type
  const setSearchType = useCallback(
    (searchType: SearchType): void => {
      setState((prev) => ({
        ...prev,
        searchType,
        searchTypeLabel: SEARCH_TYPE_LABELS[searchType],
      }))
      updateURLParams({ searchType })
    },
    [updateURLParams]
  )

  // Set query
  const setQuery = useCallback(
    (query: string): void => {
      setState((prev) => ({ ...prev, query }))
      updateURLParams({ query })
    },
    [updateURLParams]
  )

  // Set filters
  const setFilters = useCallback(
    (filters: MobileSearchFilters): void => {
      setState((prev) => ({ ...prev, filters }))
      updateURLParams({ filters })
    },
    [updateURLParams]
  )

  // Set sort
  const setSort = useCallback(
    (sort: string): void => {
      setState((prev) => ({ ...prev, sort }))
      updateURLParams({ sort })
    },
    [updateURLParams]
  )

  // Remove individual filter value
  const removeFilter = useCallback(
    (filterKey: 'category' | 'type', valueToRemove?: string): void => {
      setState((prev) => {
        const currentValue = prev.filters[filterKey]

        let newFilterValue: string | undefined

        // If no specific value provided, remove entire filter
        if (!valueToRemove || !currentValue) {
          newFilterValue = undefined
        } else {
          // Remove specific value from comma-separated list
          const values = currentValue.split(',').map((v) => v.trim())
          const newValues = values.filter((v) => v !== valueToRemove)
          newFilterValue =
            newValues.length > 0 ? newValues.join(',') : undefined
        }

        // Update URL with the computed filter value
        updateURLParams({
          filters: {
            [filterKey]: newFilterValue,
          },
        })

        return {
          ...prev,
          filters: {
            ...prev.filters,
            [filterKey]: newFilterValue,
          },
        }
      })
    },
    [updateURLParams]
  )

  // Clear search
  const clearSearch = useCallback((): void => {
    setState((prev) => ({
      ...prev,
      query: '',
      results: [],
      total: 0,
      error: null,
    }))
    updateURLParams({ query: '' })
  }, [updateURLParams])

  // Load more results
  const loadMore = useCallback((): void => {
    executeSearch(
      state.searchType,
      state.query,
      state.filters,
      state.sort,
      true
    )
  }, [executeSearch, state.searchType, state.query, state.filters, state.sort])

  return {
    state,
    setSearchType,
    setQuery,
    setFilters,
    setSort,
    removeFilter,
    clearSearch,
    loadMore,
  }
}
